import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { getMySubscription, setMySubscription } from '../../lib/emailSubscription'
import {
  ASKED_STORAGE_KEY, canRemember, markAsked, wasAsked,
} from '../../lib/signupEmailPrompt'
import { claimRootPrompt, releaseRootPrompt } from '../../lib/rootPrompt'
import { supabase } from '../../lib/supabase'
import { trackEvent } from '../../lib/analytics'
import { useCommonT } from '../../i18n'
import { OVERLAY_ABOVE_AD, PANEL_FITS_OVERLAY } from '../../lib/adInset'
import { useModalFocus } from '../../hooks/useModalFocus'

/**
 * "Want product updates?", asked once per account - every signed-in user, not
 * only new signups, so the existing user base sees it once on their next visit.
 *
 * It lives at the app root rather than in the sign-in modal because most people
 * who see it are already signed in, and because three of the six signup methods
 * (Google, Apple, Discord) leave the site entirely while the password path has
 * no session to write consent with until the confirmation link is tapped. By
 * the time there IS a session to ask from, the modal is long gone.
 *
 * Nothing is pre-selected and nothing is written until a button is pressed.
 * "No thanks" writes nothing at all, which leaves the account exactly where it
 * started, and the /account toggle is still there for anyone who changes their
 * mind. That is also why declining is remembered locally: see
 * lib/signupEmailPrompt.
 *
 * The gate is deliberately three-part and ordered cheapest-first - a brand-new
 * account, not already asked on this device, and only THEN the RPC that says
 * whether they have already decided elsewhere. An existing subscriber who signs
 * up on a second device is never asked.
 */

type Phase = 'ask' | 'busy' | 'error' | 'thanks'

export default function SignupEmailPrompt() {
  const { user } = useAuth()
  // The account this card is FOR. Everything is recorded against it, and the
  // card unmounts the moment the session is somebody else: a prompt opened for
  // one account must never write consent for another.
  const [pending, setPending] = useState<{ userId: string } | null>(null)
  // eslint-disable-next-line i18next/no-literal-string -- state code
  const [phase, setPhase] = useState<Phase>('ask')
  const t = useCommonT()
  // The decision in flight, per user id. A plain "already decided" ref does not
  // survive StrictMode, which runs setup, cleanup, then setup again: the replay
  // saw the id as decided and returned, and the first attempt had already been
  // cancelled, so the card never opened in a dev build at all.
  const decision = useRef<{ userId: string; settled: boolean } | null>(null)
  // The live session, readable from inside an await. The closure's `user` is
  // the one from the render that started the call.
  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    if (!user) {
      // eslint-disable-next-line i18next/no-literal-string -- root prompt id
      releaseRootPrompt('email-optin')
      setPending(null)
      return
    }
    // Signed in as somebody else while the card was open.
    setPending(prev => {
      if (prev && prev.userId !== user.id) {
        // eslint-disable-next-line i18next/no-literal-string -- root prompt id
        releaseRootPrompt('email-optin')
        return null
      }
      return prev
    })

    if (decision.current?.userId === user.id) return
    const mine = { userId: user.id, settled: false }
    decision.current = mine

    // No address to mail. `set_my_email_subscription` answers 'no_email' for
    // these, so the card would be a dead end (iOS excludes its anonymous
    // sessions the same way).
    if (!user.email) return
    if (wasAsked(user.id)) return
    // An answer this browser cannot store is an answer it cannot honour: the
    // card would come back on every load, which is worse than never asking.
    if (!canRemember()) return

    let cancelled = false
    void (async () => {
      const status = await readStatusFor(user.id)
      mine.settled = true
      if (cancelled || status !== 'none') return
      if (userRef.current?.id !== user.id) return
      // Yields to any other root prompt on this load, and records nothing if a
      // more important one arrives later - nothing was answered, so the next
      // load asks again.
      // eslint-disable-next-line i18next/no-literal-string -- root prompt id
      if (!claimRootPrompt('email-optin', () => setPending(null))) return
      // eslint-disable-next-line i18next/no-literal-string -- state code
      setPhase('ask')
      setPending({ userId: user.id })
    })()
    return () => {
      cancelled = true
      // Let StrictMode's replay redo a decision that never finished.
      if (!mine.settled && decision.current === mine) decision.current = null
    }
  }, [user])

  // Another tab answered for this account. Close rather than leave a second
  // copy of a question that has been answered.
  useEffect(() => {
    if (!pending) return
    const onStorage = (event: StorageEvent) => {
      if (event.key !== ASKED_STORAGE_KEY) return
      if (wasAsked(pending.userId)) {
        // eslint-disable-next-line i18next/no-literal-string -- root prompt id
        releaseRootPrompt('email-optin')
        setPending(null)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [pending])

  // One layer of the shared modal stack (useModalFocus): top-only Escape and
  // Tab, focus in and back, covered while another layer is above.
  // Escape is the backdrop's dismiss.
  const showing = !!pending && !!user && user.id === pending.userId
  const layerRef = useRef<HTMLDivElement>(null)
  const dismissRef = useRef(() => {})
  useModalFocus(showing, layerRef, () => dismissRef.current())

  if (!pending || !user || user.id !== pending.userId) return null

  const forUser = pending.userId

  const close = () => {
    // eslint-disable-next-line i18next/no-literal-string -- root prompt id
    releaseRootPrompt('email-optin')
    setPending(null)
  }

  const decline = () => {
    if (phase === 'busy') return
    markAsked(forUser)
    trackEvent('email_prompt_declined', { source: 'signup_prompt' })
    close()
  }

  // Backdrop. After a successful opt-in the question is answered, so this
  // closes rather than recording a second, contradictory outcome.
  const dismiss = () => {
    if (phase === 'thanks') close()
    else decline()
  }
  dismissRef.current = dismiss

  const accept = async () => {
    if (phase === 'busy') return
    // eslint-disable-next-line i18next/no-literal-string -- state code
    setPhase('busy')
    // The token is resolved HERE and passed to the RPC, so the write cannot be
    // redirected onto a session that replaced this one mid-flight. A stale
    // token also refreshes on the retry this way.
    const accessToken = await tokenFor(forUser)
    // eslint-disable-next-line i18next/no-literal-string -- state code
    if (!accessToken) { setPhase('error'); return }
    const result = await setMySubscription(true, accessToken)
    if (userRef.current?.id !== forUser) { close(); return }
    if (result === 'confirmed') {
      markAsked(forUser)
      trackEvent('email_subscription_opted_in', { source: 'signup_prompt' })
      // eslint-disable-next-line i18next/no-literal-string -- state code
      setPhase('thanks')
    } else {
      // Includes 'no_email' and 'unsubscribed', neither of which is the consent
      // the user just gave. Keep the card open rather than claim a success they
      // would not be able to verify in their inbox.
      // eslint-disable-next-line i18next/no-literal-string -- state code
      setPhase('error')
    }
  }

  return (
    <div
      ref={layerRef}
      tabIndex={-1}
      className={`fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 ${OVERLAY_ABOVE_AD}`}
      onClick={dismiss}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="signup-email-prompt-title"
        className={`bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-sm w-full space-y-4 ${PANEL_FITS_OVERLAY}`}
        onClick={e => e.stopPropagation()}
      >
        {phase === 'thanks' ? (
          <>
            <h2 id="signup-email-prompt-title" className="text-lg font-semibold text-gray-100">
              {t('signup_email.thanks_title')}
            </h2>
            <p className="text-sm text-gray-300">{t('signup_email.thanks_body')}</p>
            <button
              type="button"
              onClick={close}
              className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              {t('signup_email.done')}
            </button>
          </>
        ) : (
          <>
            <h2 id="signup-email-prompt-title" className="text-lg font-semibold text-gray-100">
              {t('signup_email.title')}
            </h2>
            <p className="text-sm text-gray-300">{t('signup_email.body')}</p>
            {phase === 'error' && (
              <p className="text-xs text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded p-2" role="alert">
                {t('signup_email.error')}
              </p>
            )}
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => void accept()}
                disabled={phase === 'busy'}
                className="w-full px-4 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {phase === 'busy' ? t('signup_email.saving') : t('signup_email.yes')}
              </button>
              <button
                type="button"
                onClick={decline}
                disabled={phase === 'busy'}
                className="w-full px-4 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-sm hover:bg-gray-700 disabled:opacity-50 transition-colors"
              >
                {t('signup_email.no')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The access token of the session belonging to `userId`, or null if the current
 * session is somebody else's or unreadable. Every RPC this component makes is
 * addressed with the token this returns.
 */
async function tokenFor(userId: string): Promise<string | null> {
  if (!supabase) return null
  try {
    const { data } = await supabase.auth.getSession()
    const session = data?.session
    if (!session || session.user?.id !== userId) return null
    return session.access_token ?? null
  } catch {
    return null
  }
}

/** Subscription status, read on that account's own token. */
async function readStatusFor(userId: string) {
  const accessToken = await tokenFor(userId)
  if (!accessToken) return null
  return getMySubscription(accessToken)
}
